import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import type { NewSpaceAccessDto, ResourceType } from '../../../shared/types/access';

/**
 * What the app shell needs from each tool (boards, retros, goal spaces).
 * Each feature folder exports one manifest from its index.tsx; see features/index.ts.
 */
export interface FeatureManifest {
    type: ResourceType;
    /** "Collab board" */
    label: string;
    /** "Collab boards" */
    plural: string;
    /** One line for the create tile on Home. */
    tagline: string;
    /** Icon component; rendered at ~24px. */
    Icon: ComponentType<{ fontSize?: 'small' | 'medium' | 'large' | 'inherit' }>;
    /** Accent colour for this tool's tiles and chips. */
    accent: string;
    /** URL prefix, e.g. '/b'. The resource lives at `${pathPrefix}/${shareId}`. */
    pathPrefix: string;
    /** Routes under the app shell. Paths are absolute, e.g. '/b/:shareId'. */
    routes: RouteObject[];
    /** Download the feature's page code ahead of time, so opening a space doesn't wait for it. */
    preload?: () => Promise<unknown>;
    /**
     * Create a resource with this title and resolve with its shareId. Home calls this from the
     * shared "New ..." dialog, then navigates to `${pathPrefix}/${shareId}`.
     */
    /** `access`: the CoSparks Workspace it goes in (null = personal) and who can open it. */
    create(title: string, access: NewSpaceAccessDto): Promise<{ shareId: string }>;
    /**
     * Spaces with a banner image: the "New ..." dialog offers one, and calls this with the cropped image right after
     * creating the space (images upload to an existing space). Rejects with a message to show when it fails.
     */
    setBanner?: (shareId: string, file: File) => Promise<void>;
    /** Bring an archived space back (its owner, from Home's Archived list). */
    restore: (shareId: string) => Promise<void>;
}

export function resourcePath(manifest: Pick<FeatureManifest, 'pathPrefix'>, shareId: string) {
    return `${manifest.pathPrefix}/${shareId}`;
}
